/**
 * Deployment environment helpers (Zuvo Standard, section 3).
 *
 * production: indexable landing page, analytics on.
 * test: never indexed, no analytics, no sitemap, no llms.txt.
 * development: local machine, behaves like test.
 */

export type DeploymentEnv = "production" | "test" | "development";

export const DEPLOYMENT_ENVS: readonly DeploymentEnv[] = ["production", "test", "development"];

/** Value of the `X-Robots-Tag` header (and robots meta tag) on every non-indexable host. */
export const NOINDEX_HEADER = "noindex, nofollow, noarchive, nosnippet, noimageindex";

/** Parses `APP_ENV` (or `PUBLIC_APP_ENV`). Anything unknown or missing is "development". */
export function parseDeploymentEnv(value: string | undefined): DeploymentEnv {
  const normalized = value?.trim().toLowerCase();
  return DEPLOYMENT_ENVS.find((env) => env === normalized) ?? "development";
}

/** Only production may be indexed by search engines and AI crawlers. */
export function isIndexable(env: DeploymentEnv): boolean {
  return env === "production";
}

/** Google Tag Manager loads only in production and only when a container id is configured. */
export function shouldLoadAnalytics(env: DeploymentEnv, gtmId: string | undefined | null): boolean {
  return env === "production" && typeof gtmId === "string" && gtmId.trim() !== "";
}

/* ---------- Environment flag ---------- */

/**
 * The visual flag on every non-production page (test and the local machine), so nobody mistakes
 * test for the live product: a coloured stripe along the top edge with a small label. It never
 * takes clicks (`pointer-events: none`) and is hidden when printing. Components:
 * `@zuvo-ch/kit/react/EnvironmentFlag` and `@zuvo-ch/kit/astro/EnvironmentFlag`.
 */
export type EnvFlagLocale = "de" | "en";

export const ENV_FLAG_LABELS: Record<Exclude<DeploymentEnv, "production">, Record<EnvFlagLocale, string>> = {
  test: { de: "Testumgebung", en: "Test environment" },
  development: { de: "Lokal", en: "Local" },
};

export const ENV_FLAG_TITLES: Record<Exclude<DeploymentEnv, "production">, Record<EnvFlagLocale, string>> = {
  test: { de: "Testumgebung, nicht die Live-Version", en: "Test environment, not the live version" },
  development: { de: "Lokale Entwicklung", en: "Local development" },
};

/** Label and title of the flag, or null in production (no flag). */
export function envFlag(
  env: DeploymentEnv,
  locale: EnvFlagLocale = "de",
): { env: Exclude<DeploymentEnv, "production">; label: string; title: string } | null {
  if (env === "production") return null;
  return { env, label: ENV_FLAG_LABELS[env][locale], title: ENV_FLAG_TITLES[env][locale] };
}

/** Stable class name and `<style href>` of the flag styles (deduplicated by React 19). */
export const ENV_FLAG_CLASS = "zuvo-env-flag";

/** Self-contained styles: amber for test, blue for the local machine, same in light and dark mode. */
export const ENV_FLAG_CSS = `
.zuvo-env-flag{--zef-bg:#f59e0b;--zef-fg:#1c1917;position:fixed;inset:0 0 auto 0;z-index:2147483647;
height:3px;background:var(--zef-bg);pointer-events:none;
font:600 11px/1 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;letter-spacing:.04em;text-transform:uppercase}
.zuvo-env-flag[data-env="development"]{--zef-bg:#1d4ed8;--zef-fg:#fff}
.zuvo-env-flag__label{position:absolute;top:3px;left:50%;transform:translateX(-50%);padding:3px 10px 4px;
border-radius:0 0 6px 6px;background:var(--zef-bg);color:var(--zef-fg);white-space:nowrap}
@media print{.zuvo-env-flag{display:none}}
`.trim();
